import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { BadgeCheck, FileSignature, Plus, ThumbsUp, Users } from "lucide-react";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { SearchInput } from "@/components/shared/SearchInput";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePartnerRecommendations, usePartnerStudents } from "@/hooks/usePartnerData";

export const Route = createFileRoute("/partner/recommendations/")({
  component: PartnerRecommendations,
});

type PartnerStudent = {
  id: string | number;
  name: string;
};

type RecommendationRecord = {
  id: string | number;
  student_id: string | number;
  studentName?: string;
  status?: string;
  content: string;
  created_at?: string;
};

type MappedRecommendation = RecommendationRecord & {
  studentName: string;
  statusLabel: string;
  dateLabel: string;
};

function PartnerRecommendations() {
  const [search, setSearch] = useState("");
  const { data: recommendations = [] } = usePartnerRecommendations();
  const { data: students = [] } = usePartnerStudents();

  const mappedRecommendations = useMemo(
    () =>
      recommendations
        .map((rec: RecommendationRecord) => {
          const student = students.find(
            (item: PartnerStudent) => String(item.id) === String(rec.student_id),
          );
          return {
            ...rec,
            studentName: rec.studentName || student?.name || `Student #${rec.student_id}`,
            statusLabel: (rec.status || "submitted").replace(/_/g, " "),
            dateLabel: rec.created_at
              ? new Date(rec.created_at).toLocaleDateString("en-GB")
              : "N/A",
          };
        })
        .filter(
          (item: MappedRecommendation) =>
            item.studentName.toLowerCase().includes(search.toLowerCase()) ||
            item.content.toLowerCase().includes(search.toLowerCase()),
        ),
    [recommendations, search, students],
  );

  const highlyRecommended = mappedRecommendations.filter((item: MappedRecommendation) =>
    (item.status || "").includes("high"),
  ).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Career endorsements"
        subtitle="Track submitted recommendations and maintain a professional record of employer-backed student performance."
        breadcrumbs={[
          { label: "Dashboard", path: "/partner/dashboard" },
          { label: "Recommendations" },
        ]}
        action={
          <Button asChild>
            <Link to="/partner/recommendations/new">
              <Plus />
              New endorsement
            </Link>
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard
          label="Total endorsements"
          value={mappedRecommendations.length}
          icon={<ThumbsUp />}
        />
        <StatCard label="Highly recommended" value={highlyRecommended} icon={<BadgeCheck />} />
        <StatCard label="Student pool" value={students.length} icon={<Users />} />
      </div>

      <SectionCard
        title="Professional ledger"
        actions={
          <SearchInput
            placeholder="Search by student or comment..."
            value={search}
            onChange={setSearch}
          />
        }
        noPadding
      >
        {mappedRecommendations.length === 0 ? (
          <EmptyState
            bare
            title="No recommendations yet"
            description="Submit your first recommendation to start building employer-backed student records."
            actionLabel="Create recommendation"
            onAction={() => (window.location.href = "/partner/recommendations/new")}
          />
        ) : (
          <Table className="min-w-[680px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Student</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Recommendation note</TableHead>
                <TableHead className="text-right">Trace</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappedRecommendations.map((rec: MappedRecommendation) => (
                <TableRow key={rec.id}>
                  <TableCell className="font-medium text-foreground">{rec.studentName}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="capitalize">
                      {rec.statusLabel}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-caption text-muted-foreground tabular-nums">
                    {rec.dateLabel}
                  </TableCell>
                  <TableCell className="max-w-[420px] leading-6 whitespace-normal text-body">
                    {rec.content}
                  </TableCell>
                  <TableCell className="text-right">
                    <FileSignature
                      className="ml-auto size-4 text-muted-foreground"
                      aria-hidden
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
    </div>
  );
}
